import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

/**
 * Feedback tab, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v2) — mirrors
 * `apps/admin-web/src/pages/FeedbackPage.tsx` (itself mirroring `apps/admin/app/(tabs)/feedback.tsx` /
 * `specs/admin/screens/feedback.md`): a read-only list of submitted feedback messages. The `useQuery`
 * here is lifted UNCHANGED — only the returned JSX differs.
 */
export function FeedbackPage() {
  const { client, token } = useAuth();

  const feedbackQuery = useQuery({
    queryKey: ["admin-feedback"],
    queryFn: () => client!.adminFeedback(token!),
    enabled: !!client && !!token,
  });
  const feedback = feedbackQuery.data ?? [];

  return (
    <MainCard title="Feedback">
      {feedbackQuery.isError ? <Alert severity="error">{(feedbackQuery.error as Error).message}</Alert> : null}
      {feedbackQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : feedback.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 0 }}>
          No feedback submitted yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {feedback.map((f) => (
            <Box key={f.id} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography sx={{ mb: 0.5 }}>{f.message}</Typography>
              <Typography variant="body2" color="text.secondary">
                {f.email ?? "Anonymous"} · {new Date(f.createdAt * 1000).toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </MainCard>
  );
}
